import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { ISwitchAutomationStep } from '../../../../extViewmodels/sdk/models';
import { AutomationStepAction, isSwitchStep } from '../../../../models/Automations';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { useEventLogging } from '../../../../models/Logging';
import { useAutomationTemplateStepSwitchRemoveCaseMutation } from '../../../../queries/Automation/mutations/switchCases/useAutomationTemplateStepSwitchRemoveCaseMutation';
import { useAutomationTemplateStepSwitchUpdateCaseMutation } from '../../../../queries/Automation/mutations/switchCases/useAutomationTemplateStepSwitchUpdateCaseMutation';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { useAutomationEditorContext } from '../../../containers/EditAutomationTemplate/hooks';
import { navigation } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationTemplateStepIndicatorIcon } from '../../svgs/icons/AutomationTemplateStepIndicator';
import { EditItemPenIcon } from '../../svgs/icons/EditItemPenIcon';
import { RoundAddIcon } from '../../svgs/icons/RoundAddIcon';
import { TextInputModal } from '../../TextInputModal';
import { EditAutomationTemplateStepCardHeader } from '../AutomationStepCard/presentation';
import { EditAutomationTemplateItemBumpers } from '../EditAutomationTemplateItemBumpers';
import { EditAutomationTemplateSegmentHeader } from '../EditAutomationTemplateSegmentHeader';
import { EditAutomationTemplateSteps } from '../EditAutomationTemplateSteps';
import { styleSheet } from './styles';

interface IEditAutomationTemplateSegmentProps {
	caseStatement: Api.IAutomationStepCaseStatement;
	parentAutomationTemplateId?: string;
	switchStep?: Api.ISwitchAutomationStep;
	onStepSaved: () => void;
}

export const EditAutomationTemplateSegment: React.FC<IEditAutomationTemplateSegmentProps> = props => {
	const { parentAutomationTemplateId, switchStep, caseStatement, onStepSaved } = props;
	const { automationTemplate, impersonationContext, steps, executeActionForStep, addStep } =
		useAutomationEditorContext();

	// Use the queried template data, falling back to legacy sources
	const parentAutomationTemplate = automationTemplate || (caseStatement as any)?.automationTemplate;

	// Use the custom hook for managing steps

	// Update all of this
	const { logApiError, logEvent } = useEventLogging('EditAutomationSegment');
	const errorMessages = useErrorMessages();
	const index = caseStatement
		? (switchStep?.cases?.findIndex((x: Api.IAutomationStepCaseStatement) => x.id === caseStatement.id) ?? -1)
		: -1;

	// React Query mutations for case operations
	const removeCaseMutation = useAutomationTemplateStepSwitchRemoveCaseMutation({
		onSuccess: () => {
			onStepSaved?.();
		},
		onError: error => {
			logApiError('RemoveSwitchStepCase-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	// Use ref to avoid infinite loop - mutation objects are unstable references
	const removeCaseMutationRef = React.useRef(removeCaseMutation);
	removeCaseMutationRef.current = removeCaseMutation;

	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation({
		onSuccess: () => {
			onStepSaved?.();
		},
		onError: error => {
			logApiError('UpdateSwitchStepCase-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const isLoaded = !!automationTemplate && steps.length > 0;

	const onAddStepClicked = React.useCallback(() => {
		addStep();
		onStepSaved?.();
	}, [addStep, onStepSaved]);

	const onMenuItemClicked = React.useCallback(
		async (action: AutomationStepAction) => {
			switch (action) {
				case AutomationStepAction.Delete: {
					const _isSwitchStepOrNull = isSwitchStep(switchStep) || switchStep === null;

					const nonDefaultSteps =
						(switchStep as ISwitchAutomationStep)?.cases?.filter(
							(x: Api.IAutomationStepCaseStatement) => !x.isDefault
						) || [];
					if (nonDefaultSteps.length === 1) {
						// bubble up and remove the switch step
						try {
							logEvent('RemoveSwitchStep');
							await executeActionForStep(switchStep, action);
							onStepSaved?.();
						} catch (error) {
							logApiError('RemoveSwitchStep-Error', error);
							errorMessages.pushApiError(error);
						}
					} else {
						// remove just the case/segment
						logEvent('RemoveSwitchStepCase', {
							caseStatementId: caseStatement.id,
						});

						if (parentAutomationTemplateId && switchStep?.id && caseStatement.id) {
							await removeCaseMutationRef.current.mutateAsync({
								templateId: parentAutomationTemplateId,
								stepId: switchStep.id,
								caseId: caseStatement.id,
								impersonationContext,
							});
						}
					}
					break;
				}
				default: {
					break;
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			switchStep,
			executeActionForStep,
			caseStatement,
			parentAutomationTemplateId,
			onStepSaved,
			logEvent,
			logApiError,
			errorMessages,
		]
	);
	// #endregion

	// #region rename modal
	const [renameModalProps, setRenameModalProps, setRenamModalLambda] = useLambda(
		{
			isOpen: false,
			onRequestClose: null,
		},
		[]
	);
	const renameModalPropsRef = React.useRef(renameModalProps);
	renameModalPropsRef.current = renameModalProps;

	// Use ref to avoid infinite loop - mutation objects are unstable references
	const updateCaseMutationRef = React.useRef(updateCaseMutation);
	updateCaseMutationRef.current = updateCaseMutation;

	const onRenameModalRequestClose = React.useCallback(
		async (nextName?: string, canceled?: boolean) => {
			const name = nextName?.trim();
			setRenameModalProps({
				...renameModalPropsRef.current,
				isOpen: false,
			});
			if (!canceled && !!name) {
				logEvent('RenameSegment', {
					caseStatementId: caseStatement?.id,
					nameLength: name.length,
				});

				if (parentAutomationTemplateId && switchStep?.id && caseStatement?.id) {
					await updateCaseMutationRef.current.mutateAsync({
						templateId: parentAutomationTemplateId,
						stepId: switchStep.id,
						caseId: caseStatement.id,
						caseData: {
							...(caseStatement || {}),
							name,
						},
						impersonationContext,
					});
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[caseStatement, switchStep, parentAutomationTemplateId, logEvent, setRenameModalProps]
	);
	React.useEffect(() => {
		setRenameModalProps({
			...renameModalPropsRef.current,

			onRequestClose: onRenameModalRequestClose,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onRenameModalRequestClose]);
	// #endregion

	const onRenderTitle = React.useCallback(() => {
		return (
			<div className={css(baseStyleSheet.horizontalStack)}>
				<div className={css(baseStyleSheet.truncateText, styleSheet.cardHeaderTitleName)}>
					{`Segment ${index + 1}: ${caseStatement?.name || 'Add name'}`}
				</div>
				<button
					className={css(styleSheet.segmentEditNameButton)}
					onClick={setRenamModalLambda({ ...renameModalProps, isOpen: true })}
				>
					<EditItemPenIcon />
				</button>
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renameModalProps, caseStatement?.name, index]);

	const styles = React.useRef([styleSheet.segment]).current;

	// TODO: Compute this
	const canEdit = false;

	// Segments always use the switch step icon since they're children of a switch step
	const segmentIconStep = React.useMemo(() => {
		return { _type: Api.AutomationStepType.Switch } as any;
	}, []);

	const onRenderLeftBumper = React.useCallback(() => {
		return (
			<div className={css(styleSheet.indicator)}>
				<AutomationTemplateStepIndicatorIcon
					step={segmentIconStep}
					type='default'
					styles={[styleSheet.indicatorIcon]}
				/>
				<div className={css(styleSheet.indicatorHorizontalLine)} />
				<div className={css(styleSheet.indicatorVerticalLine, styleSheet.indicatorVerticalLineBottom)} />
				<div className={css(styleSheet.indicatorVerticalLine, styleSheet.indicatorVerticalLineTop)} />
			</div>
		);
	}, [segmentIconStep]);

	const cardHeaderStyles = React.useRef<StyleDeclarationValue[]>([styleSheet.segmentCardHeader]).current;
	return (
		<EditAutomationTemplateItemBumpers
			onRenderLeftBumperContent={onRenderLeftBumper}
			scrollAnchorId={caseStatement?.id}
			styles={styles}
		>
			<EditAutomationTemplateStepCardHeader
				canEdit={canEdit}
				ActionMenu={
					caseStatement?.isDefault ? null : (
						<Dropdown>
							<DropdownEllipsisTrigger />
							<DropdownContent>
								<DropdownItem onClick={() => onMenuItemClicked(AutomationStepAction.Delete)}>
									Delete Segment
								</DropdownItem>
							</DropdownContent>
						</Dropdown>
					)
				}
				onRenderTitle={onRenderTitle}
				styles={cardHeaderStyles}
			/>
			{isLoaded ? (
				<>
					<EditAutomationTemplateSegmentHeader
						templateId={parentAutomationTemplateId}
						stepId={switchStep?.id}
						impersonationContext={impersonationContext}
						caseStatement={caseStatement}
						switchStep={switchStep}
						onStepSaved={onStepSaved}
					/>
					<div className={css(styleSheet.segmentSteps)}>
						<EditAutomationTemplateSteps
							onStepSaved={onStepSaved}
							automationTemplate={parentAutomationTemplate}
							impersonationContext={impersonationContext}
						/>
					</div>
					<div className={css(styleSheet.segmentStepsFooter)}>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.segmentAddStepButton)}
							onClick={onAddStepClicked}
						>
							<RoundAddIcon fillColor={navigation} />
							<span>Add another step for this segment</span>
						</button>
					</div>
				</>
			) : (
				<LoadingSpinner type='small' />
			)}

			{index >= 0 && (
				<TextInputModal
					cta='Rename'
					initialValue={caseStatement?.name}
					modalProps={renameModalProps}
					placeholderText='Please enter a name...'
					title={`Rename Segment ${index + 1}`}
					type='text'
				/>
			)}
		</EditAutomationTemplateItemBumpers>
	);
};
